import {
  type Locale,
  localeForCountry,
  type MessageKey,
  type MessageVars,
  type PluralKey,
  resolveLocale,
} from "@colorglo/i18n";
import { plural, t } from "@colorglo/i18n/catalogs/staff";
import { useCallback, useMemo } from "react";
import { useMe } from "../api/hooks.ts";
import { pickFranchise, storedFranchiseId } from "./franchise.ts";

/**
 * The signed-in user's language, and `t` bound to it.
 *
 * P12 item 3's chain, in the order that is right for a person with an account:
 *
 *   1. `users.locale` from `GET /v1/me` — the answer they actually gave us. A Norwegian
 *      technician who set their account to `no` should see Norwegian on a shared workshop
 *      tablet whose OS is Swedish.
 *   2. the country of the franchise they are looking at — the better default than the
 *      browser for a new account at the Trondheim workshop.
 *   3. `navigator.language`, for a session that has not loaded yet.
 *   4. `sv` (BUILD.md §1.2 — the default is Swedish, not English).
 *
 * Country sits *below* the user's own setting and not above it, even though the phase prompt
 * lists it first: a franchise's country is a default, and a preference that a default can
 * override is not a preference.
 */

export function useLocale(): Locale {
  const me = useMe();
  const franchise = pickFranchise(me.data?.franchises ?? [], storedFranchiseId());

  return useMemo(
    () =>
      resolveLocale([
        me.data?.user.locale,
        localeForCountry(franchise?.country_code),
        navigator.language,
      ]),
    [me.data?.user.locale, franchise?.country_code],
  );
}

export type Translate = (key: MessageKey, vars?: MessageVars) => string;

/**
 * `t` with the locale already applied.
 *
 * Components call `t("calendar.today")` and never pass a locale, which is what stops a
 * component from rendering half its strings in Swedish and half in the user's language
 * because someone forgot an argument. Stable across renders, so it can sit in a dependency
 * array without re-running everything that reads it.
 */
export function useT(): Translate {
  const locale = useLocale();
  return useCallback((key: MessageKey, vars?: MessageVars) => t(key, locale, vars), [locale]);
}

export type Pluralize = (key: PluralKey, count: number, vars?: MessageVars) => string;

/**
 * The counted counterpart to `useT`, for the strings whose noun changes with the number.
 *
 * The key given is the stem — `plural("network.subtitle", 1)` reads `network.subtitle.one` —
 * and the count arrives formatted for the locale, so nothing at a call site has to know
 * either which plural forms a language has or how it groups its digits.
 */
export function usePlural(): Pluralize {
  const locale = useLocale();
  return useCallback(
    (key: PluralKey, count: number, vars?: MessageVars) => plural(key, count, locale, vars),
    [locale],
  );
}
